/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import { desktopHeaderHeight, mobileHeaderHeight } from 'hooks/useHeaderHeights';

import AnimatedScrollDown from 'assets/icons/scroll/AnimatedScrollDown';
import BackgroundSwitch from 'components/background/BackgroundSwitch';
import Breadcrumbs from 'components/Breadcrumbs';
import ColumnsLayout from 'components/contentLayouts/ColumnsLayout';
import GiftCertificateContent from 'components/pages/checkout/GiftCertificateContent';
import { GiftCertificateProvider } from 'libs/GiftCertificate/GiftCertificateContext';
import Heading from 'components/text/Heading';
import HeroWrapper from 'components/wrappers/HeroWrapper';
import MaxWidthWrapper from 'components/wrappers/MaxWidthWrapper';
import Paragraph from 'components/text/Paragraph';
import PropTypes from 'prop-types';
import React from 'react';
import { TextLinkWysisyg } from 'components/text/TextLink';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import { above } from 'utils/mediaqueries';
import colors from 'config/theme/colors';
import decodeHTMLEntities from '@nwg/core-web/utils/decodeHTMLEntities';
import { mediaQueries } from 'config/theme/breakpoints';
import styled from 'libs/styled';
import transformFlexibleBackground from 'libs/wordpress/utils/transformFlexibleBackground';
import useAboveBreakpoint from 'hooks/useAboveBreakpoint';

const Div = styled('div')``;

const Overlay = styled('div')`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    background-color: ${colors.black};
    opacity: 0.1;
`;

const Row = styled('div')`
    position: relative;
    display: flex;
    height: 100%;
    margin: 0px -12px;
`;

const ContentBox = styled('div')`
    position: relative;
    width: 83.333%;
    margin-top: auto;
    padding: 0px 12px 24px 12px;
    z-index: 1;

    ${above.sm} {
        width: 50%;
    }

    ${above.md} {
        width: 41.666%;
        margin-bottom: 56px;
    }
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
    position: absolute;
    top: ${mobileHeaderHeight}px;
    padding: 12px 0px 0px 12px;

    ${above.md} {
        top: ${desktopHeaderHeight}px;
        padding-top: 16px;
    }
`;

const GiftCertificate = ({ data }) => {
    const isAboveSm = useAboveBreakpoint('sm');

    const { content, breadcrumbs, title } = data;
    const {
        background_media_flexible: background,
        heading,
        preamble_paragraph: preamble,
        paragraph,
        text_color: textColor,
        useOverlay,
    } = content || {};

    const transformedBackground = transformFlexibleBackground(background);
    const mobileBackground = transformedBackground.mobile || transformedBackground.desktop;
    const desktopBackground = transformedBackground.desktop || transformedBackground.mobile;

    return (
        <>
            <AnimatedScrollDown
                position="absolute"
                color={textColor}
                right={['12px', null, null, '32px']}
                bottom="32px"
                zIndex={5}
            />
            <HeroWrapper as="section">
                <BackgroundSwitch
                    type={isAboveSm ? desktopBackground.type : mobileBackground.type}
                    background={isAboveSm ? desktopBackground : mobileBackground}
                    mediaQuery={mediaQueries.fullWidth}
                >
                    {useOverlay && <Overlay />}
                    <MaxWidthWrapper includeContentMargins color={textColor} height="100%" display="flex">
                        <Row>
                            <StyledBreadcrumbs currentTitle={title} items={breadcrumbs} textColor={textColor} />
                            <ContentBox>
                                {heading.text && (
                                    <Heading as={heading.type} m={['0 0 32px 0', null, null, '0 0 56px 0']} size="lg">
                                        {heading ? decodeHTMLEntities(heading.text) : null}
                                    </Heading>
                                )}
                                {preamble && (
                                    <Wysiwyg
                                        data={preamble}
                                        textComponent={props => <Paragraph size="xl" {...props} />}
                                        tagComponents={{ url: TextLinkWysisyg }}
                                    />
                                )}
                            </ContentBox>
                        </Row>
                    </MaxWidthWrapper>
                </BackgroundSwitch>
            </HeroWrapper>
            <MaxWidthWrapper includeContentMargins mt="56px" mb={['80px', null, '112px']}>
                <ColumnsLayout
                    blocks={[
                        <Div key="1" columnStyles={{ marginRight: 'auto' }} maxWidth={[null, null, '650px']}>
                            <Wysiwyg
                                data={paragraph}
                                textComponent={props => <Paragraph size="xl" {...props} />}
                                tagComponents={{ url: TextLinkWysisyg }}
                            />
                        </Div>,
                        <Div key="2" columnStyles={{ marginRight: 'auto' }}>
                            <GiftCertificateProvider giftCertIdsFromCms={data.gift_certificate_setup.gift_certificates}>
                                <GiftCertificateContent data={data} />
                            </GiftCertificateProvider>
                        </Div>,
                    ]}
                    columnSizes={[12, null, 6, 5]}
                    gutterHeight={64}
                />
            </MaxWidthWrapper>
        </>
    );
};

GiftCertificate.propTypes = {
    data: PropTypes.shape({
        title: PropTypes.string,
        breadcrumbs: PropTypes.arrayOf(PropTypes.object),
        content: PropTypes.object,
    }),
};

export default GiftCertificate;
